import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, Download, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";
import { fmtWhen } from "./labels";

const btnDark = "inline-flex items-center gap-2 px-4 py-2 bg-clay-900 text-white text-[12px] tracking-wider uppercase hover:bg-terracotta disabled:opacity-50";
const btnLight = "inline-flex items-center gap-2 px-4 py-2 border border-clay-300 text-clay-800 text-[12px] tracking-wider uppercase hover:border-clay-900 disabled:opacity-50";

const MODE_LABELS = { initial: "carga inicial", incremental: "nocturna/manual", cron: "nocturna", refresh: "relectura", proposals: "propuestas", bookings: "reservas", rescan: "repaso del listado" };
const JOB_LABELS = { initial: "Carga inicial", refresh: "Releyendo lead", proposals: "Leyendo propuestas", bookings: "Importando reservas", rescan: "Repasando el listado" };

export function StatusCard({ status, onRefresh, onOpenAgentMap }) {
  const [busy, setBusy] = useState(false);
  const job = status?.job;
  const running = job && ["queued", "running"].includes(job.status);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(onRefresh, 4000);
    return () => clearInterval(t);
  }, [running, onRefresh]);

  const start = async (mode) => {
    if (mode === "initial" && !(await confirmAsync("La carga inicial recorre todos los viajes de KimKim desde la fecha configurada y puede tardar un buen rato. ¿Empezar?", { confirmLabel: "Empezar" }))) return;
    setBusy(true);
    try { await api.post("/kimkim/sync", { mode }); toast.success(mode === "initial" ? "Carga inicial en marcha" : "Sincronizando…"); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar"); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="text-sm text-clay-600 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Cargando…</div>;

  return (
    <section className="border border-clay-300 bg-white p-5" data-testid="kimkim-status-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2 text-sm">
          <div><div className="smallcaps">Conexión</div><div className="mt-1" data-testid="kimkim-configured">{status.configured ? `Usuario ${status.email}` : "Sin credenciales"}</div></div>
          <div><div className="smallcaps">Última sincronización</div><div className="mt-1" data-testid="kimkim-last-sync">{fmtWhen(status.last_sync_at)}{status.last_mode ? ` · ${MODE_LABELS[status.last_mode] || status.last_mode}` : ""}</div></div>
          <div><div className="smallcaps">Resultado</div><div className="mt-1">{status.last_sync_at ? `${status.last_new ?? 0} nuevos · ${status.last_updated ?? 0} actualizados${status.last_failed ? ` · ${status.last_failed} con error` : ""}` : "—"}</div></div>
          <div><div className="smallcaps">Leads desde {status.since}</div><div className="mt-1" data-testid="kimkim-counts">{status.counts.total} · {status.counts.unassigned} sin asignar · {status.ai_analyzed} con IA{status.proposals_stored != null ? ` · ${status.proposals_stored} propuestas leídas` : ""}</div></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onOpenAgentMap} className={btnLight} data-testid="kimkim-agent-map-btn"><Users size={12}/> Agentes</button>
          {!status.initial_done_at ? (
            <button onClick={() => start("initial")} disabled={busy || running || !status.configured} className={btnDark} data-testid="kimkim-initial-btn">
              {running ? <Loader2 size={12} className="animate-spin"/> : <Download size={12}/>} Carga inicial
            </button>
          ) : (
            <button onClick={() => start("incremental")} disabled={busy || running || !status.configured} className={btnDark} data-testid="kimkim-sync-btn">
              {running ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Sincronizar ahora
            </button>
          )}
        </div>
      </div>
      {running && (
        <div className="mt-4" data-testid="kimkim-job-progress">
          <div className="flex justify-between text-[11px] text-clay-700 mb-1">
            <span>{JOB_LABELS[job.mode] || "Sincronizando"} · {job.status === "queued" || job.phase === "login" ? "entrando en KimKim…" : job.phase === "bookings" ? "leyendo la lista de reservas de KimKim…" : job.phase === "scan" ? `recorriendo el listado (${job.pages ?? 0} páginas · ${job.scanned ?? 0} viajes)` : job.phase === "proposals" ? `${job.done}/${job.total ?? "?"} leads con propuestas` : job.mode === "initial" ? `${job.done} fichas leídas, de la más reciente hacia atrás${job.skipped ? ` · ${job.skipped} fuera de rango` : ""}` : `${job.done}/${job.total ?? "?"} fichas leídas${job.skipped ? ` · ${job.skipped} fuera de rango` : ""}`}</span>
            <span>{job.new} nuevos · {job.updated} actualizados{job.proposals ? ` · ${job.proposals} propuestas` : ""}{job.failed ? ` · ${job.failed} con error` : ""}</span>
          </div>
          <div className="h-1.5 bg-clay-100"><div className={`h-1.5 bg-terracotta transition-all ${job.mode === "initial" || !job.total ? "animate-pulse" : ""}`} style={{ width: job.total && job.mode !== "initial" ? `${Math.round((100 * job.done) / job.total)}%` : "35%" }}/></div>
        </div>
      )}
      {(status.last_error || (job?.status === "error")) && (
        <div className="mt-4 text-[12px] border border-terracotta/40 bg-terracotta/5 text-clay-900 p-3 inline-flex items-start gap-2" data-testid="kimkim-error">
          <AlertTriangle size={14} className="shrink-0 mt-0.5"/> <span>{job?.status === "error" ? job.error : status.last_error}</span>
        </div>
      )}
      {!status.configured && (
        <p className="mt-4 text-[12px] text-amber-900 border border-amber-300 bg-amber-50 p-3" data-testid="kimkim-not-configured">
          Faltan las credenciales del usuario de KimKim (email y contraseña). Cuando estén guardadas, aquí aparecerá el botón de carga inicial.
        </p>
      )}
    </section>
  );
}
